import { Permission, PermissionDeniedReason, Account } from '@artup/model'
import { BuiltInFeatures } from './built-in-features'

/**
 * Service for determining permissions available to accounts
 */
export class Guardian {
  /**
   * Initializes the guardian
   * @param {Feature} features Feature tree
   * @param {Account} account Account in whose context the permissions will be resolved.
   * @param {Array[SecurityProfile]} securityProfiles Security profiles of the account, with their permissions
   * @param {Environment} environment Execution environment (DEV, PROD etc)
   */
  constructor ({ features, account, securityProfiles, environment } = {}) {
    this.created = new Date()
    this.features = features
    this.account = (account && typeof account.canUse !== 'function') ? new Account(account) : account
    this.securityProfiles = securityProfiles
    this.environment = environment
    if (this.features && this.account) {
      this.initialize()
    }
  }

  /**
   * Creates the guardian using features list from another guardian.
   * @param {Guardian} source Source guardian
   * @param {Account} account Account
   * @returns {Guardian}
   */
  static from (source, account) {
    const { features, environment } = source

    const guardian = new Guardian({
      features,
      account,
      environment
    })

    return guardian
  }

  /**
   * Initializes the guardian
   */
  initialize () {
    const { features, account, environment } = this

    if (!features) throw new Error('Features are required to initialize Guardian instance')
    if (!account) throw new Error('Account is required to initialize Guardian instance')
    if (!environment) throw new Error('Environment is required to initialize Guardian instance')
    if (!account.permissions) account.permissions = []

    this.name = `[${account.name}] Guardian`

    // Create feature list and dictionary for lookups,
    // as trees are notoriously slow to traverse ;-)
    if (features) {
      this.featureList = features.flat()
      this.featureDictionary = this.featureList.reduce((all, feature) => ({ ...all, [feature.name]: feature }), {})
    }

    // Checks the currently granted permissions
    // and marks the respective features as usable
    this.apply()
  }

  /**
   * Creation time of the service
   * @type {Date}
   */
  created

  /**
   * Age of the service, in seconds,
   * can be used for cache expiration
   * @type {Number}
   */
  get age () {
    if (this.created) {
      return ((new Date()) - this.created) / 1000
    }
  }

  /**
   * Unique name of the service
   * @type {String}
   */
  name

  /**
   * Current environment
   * @type {Environment}
   */
  environment

  /**
   * Account in whose context the permissions will be resolved
   * @type {Account}
   */
  account

  /**
   * Security profiles of the account, with their permissions
   * @type {Array[SecurityProfile]}
   */
  securityProfiles

  /**
   * Feature tree
   * @type {Feature}
   */
  features

  /**
   * Feature list, for iterations
   * @type {Array[Feature]}
   */
  featureList

  /**
   * Feature dictionary, for lookups by name
   * @type {Dictionary<string, Feature>}
   */
  featureDictionary

  /**
   * Resolved permissions
   * @type {Array[Permission]}
   */
  permissions

  /**
   * Allowed permissions
   * @type {Array[Permission]}
   */
  get allowedPermissions () {
    return (this.permissions || []).filter(p => p.canUse && p.feature.isSecure && p.feature.name !== 'root')
  }

  /**
   * Denied permissions
   * @type {Array[Permission]}
   */
  get deniedPermissions () {
    return (this.permissions || []).filter(p => !p.canUse && p.feature.isSecure && p.feature.name !== 'root')
  }

  /**
   * Resolved permissions dictionary, for quicker lookups
   * @type {Dictionary<string, Permission>}
   */
  permissionDictionary

  /**
   * Determines and returns a permission associated with
   * the specified feature. The permission's `canUse` flag will
   * indicate whether the account is allowed to use the feature.
   * @param {Feature} feature Feature to determine the permission for
   * @returns {Permission}
   * @description
   * To determine whether feature can be used, we only need to check
   * whether there exists a permission for this feature, for the account
   * as well as all his parent accounts. We don't do any other checks,
   * as all they were already done when permissions were edited.
   */
  determinePermission (feature) {
    // Firstly, feature must not be disabled globally
    const { account, securityProfiles } = this
    let permission

    // Disabled feature must never be permitted
    if (!feature.isEnabled) {
      permission = new Permission({ feature, canUse: false })
      permission.reason = permission.canUse ? undefined : PermissionDeniedReason.FeatureDisabled
      permission.details = feature.description
    }

    // The feature must be available in the current environment
    if (!permission && feature.environment) {
      const allowedEnvironments = Array.isArray(feature.environment) ? feature.environment : [feature.environment]
      const isApplicableInEnvironment = allowedEnvironments.some(e => e === this.environment)
      if (!isApplicableInEnvironment) {
        permission = new Permission({
          feature,
          canUse: false,
          reason: PermissionDeniedReason.EnvironmentDenied
        })
      }
    }

    // Super-account and integration accounts can do everything
    if (!permission && (account.isSuperAccount || account.isIntegrationAccount)) {
      permission = new Permission({ feature, canUse: true })
    }

    // Non-secure feature is permitted when all parent features are granted or also marked as not secure
    if (!permission && !feature.isSecure) {
      if (feature.parent) {
        const parentPermission = this.determinePermission(feature.parent)
        permission = new Permission({ feature, canUse: parentPermission.canUse })
        permission.reason = permission.canUse ? undefined : PermissionDeniedReason.ParentDenied
        permission.details = permission.canUse ? undefined : feature.parent.description
      } else {
        permission = new Permission({ feature, canUse: true })
      }
    }

    // At least one profile of those to which the user belongs,
    // must permit to use the feature
    if (!permission && securityProfiles) {
      const canUse = securityProfiles.some(profile => profile.canUse(feature.name))
      if (!canUse) {
        permission = new Permission({
          feature,
          canUse: false,
          reason: PermissionDeniedReason.ProfileDenied,
          details: feature.description
        })
      }
    }

    // Administrator has all the permissions of the profile
    if (!permission && account.isAdministrator) {
      permission = new Permission({ feature, canUse: true })
    }

    // In all other cases the  permission must be granted explicitly
    if (!permission) {
      const canUse = account.canUse(feature.name)
      permission = new Permission({ feature, canUse })
      if (!canUse) {
        permission.reason = PermissionDeniedReason.Denied
        permission.details = feature.description
      }
    }

    return permission
  }

  /**
   * Create a complete list of permissions for the account
   */
  apply () {
    const { featureList } = this

    const builtInPermissions = [
      new Permission({ feature: { name: BuiltInFeatures.IsSuperAccount, isSecure: true }, canUse: this.isSuperAccount }),
      new Permission({ feature: { name: BuiltInFeatures.IsIntegrationAccount, isSecure: true }, canUse: this.isIntegrationAccount }),
      new Permission({ feature: { name: BuiltInFeatures.IsAdministrator, isSecure: true }, canUse: this.isAdministrator }),
      new Permission({ feature: { name: BuiltInFeatures.IsUser, isSecure: true }, canUse: true })
    ]
    this.permissions = [
      ...builtInPermissions,
      ...featureList.map(feature => this.determinePermission(feature))
    ]

    this.permissionDictionary = this.permissions
      .reduce((all, permission) => ({ ...all, [permission.feature.name]: permission }), {})

    this.addComputedPermissions(this.features.computedPermissions)
  }

  /**
   * Adds computed permissions, as specified in
   * features.json / permissions node.
   * @param {Dictionary<string, Permission>} items Dictionary of computed permissions
   * @description Computed permissions are declared as sets of other permissions.
   * If all of the listed permissions are granted, the computed permissions is regarded as granted.
   * These permissions are obviously not available for editing in the permissions editor.
   * They're here to capture certain complex permission sets, without hard-coding them in the code.
   */
  addComputedPermissions (items = {}) {
    for (const group of Object.values(items)) {
      const items = Object
        .entries(group)
        .filter(item => typeof item[1] === 'object')

      for (const [name, { enabled, permissions = [], environment = [] }] of items) {
        if (this.permissionExists(name)) {
          throw new Error(`Cannot evaluate a computed permission [${name}], such permission already exists`)
        }
        const isSecure = permissions.length > 0 || enabled != null
        const isApplicableInEnvironment = environment.length === 0 || environment.includes(this.environment)
        const canUse = isSecure
          ? enabled !== false && isApplicableInEnvironment && this.canUseAll(permissions)
          : true

        const permission = new Permission({ feature: { name, isSecure, isEnabled: enabled !== false }, canUse, isApplicableInEnvironment })
        this.permissions.push(permission)
        this.permissionDictionary[name] = permission
      }
    }
  }

  /**
   * Indicates that account is a super account
   * @type {Boolean}
   */
  get isSuperAccount () {
    return this.account?.isSuperAccount
  }

  /**
   * Indicates that account is integration account
   * @type {Boolean}
   */
  get isIntegrationAccount () {
    return this.account?.isIntegrationAccount
  }

  /**
   * Indicates that the account is an administrator (or a super-account, which implies it)
   * @type {Boolean}
   */
  get isAdministrator () {
    return Boolean(this.account?.isAdministrator)
  }

  /**
   * Returns a permission with the specified name.
   * @param {String} name Feature name
   * @type {Permission}
   */
  getPermission (name) {
    const permission = this.permissionDictionary[name]
    if (permission) {
      return permission
    } else {
      throw new Error(`Unknown feature ${name}`)
    }
  }

  /**
   * Returns true if permission with the specified name exists.
   * @param {String} name Feature name
   * @type {Boolean}
   */
  permissionExists (name) {
    return Boolean(this.permissionDictionary[name])
  }

  /**
   * Checks whether account's level matches the level required by the feature
   * @param {Account} account Account to check
   * @param {Array[AccountLevel]} requiredLevels Feature levels required by the feature
   * @returns {Boolean}
   */
  accountHasRequiredLevel (account, requiredLevels) {
    const { level } = account
    return !requiredLevels || requiredLevels.includes(level)
  }

  /**
   * Returns true if account is allowed to use the specified feature
   * @param {String} name Feature name
   * @param {Boolean} force If true, permission must be granted explicitly.
   * Normally we skip checks for super accounts, but for some permissions which
   * are more like facts than permissions, we might require an explicit check.
   * @returns {Boolean} True if account is allowed to use the feature
   * @description The check can be negated by prefixing feature name with `no ` prefix.
   * This can be used when permissions are specified as text and checked somewhere
   * programmatically by `canUse` calls.
   * @description Normally we only verify account permission.
   * If account is using premium features and checked feature is a premium feature,
   * we also require that there is an active subscription to this premium feature.
   *
   * You can specify MUST operator before feature name,
   * which has the same effect as `force` parameter. If present,
   * permission must be granted explicitly, even for super account.
   */
  canUse (name, force) {
    if (name) {
      const { account, featureDictionary } = this
      name = name.toString().trim()
      const negate = name.toLowerCase().startsWith('not ')
      if (negate) {
        name = name.substring(4).trim()
      }
      force = force || name.toLowerCase().startsWith('must ')
      if (force) {
        name = name.substring(5).trim()
      }

      // Get the required permission
      const permission = this.permissionExists(name) ? this.getPermission(name) : null

      // Don't allow if feature marked as disabled
      if (permission?.feature?.isEnabled === false) {
        return false
      }

      // Don't allow if invalid environment (applies even to super account)
      if (permission?.isApplicableInEnvironment === false) {
        return false
      }

      // Skip all other checks if super account
      if (!force && account.isSuperAccount) {
        return negate ? false : true
      }

      // Feature must be allowed at account's level
      const feature = featureDictionary[name]
      if (feature?.level) {
        const hasRequiredLevel = this.accountHasRequiredLevel(account, feature.level)
        if (!hasRequiredLevel) {
          return negate ? true : false
        }
      }

      // Return permission status, negated if required
      if (permission) {
        const { canUse } = permission
        return Boolean(negate ? !canUse : canUse)
      }
    }
  }

  /**
   * Returns true if account is allowed to use all the specified features
   * @param {Array[String]} names Feature names
   * @param {Boolean} force If true, permission must be granted explicitly
   * @returns {Boolean} True if account is allowed to use the features
   * @description Operator prefixes are allowed before permission names,
   * which can change the default working of this function:
   *
   * NOT - negates the permission. Returns true if permission is NOT explicitly granted
   * OR  - turns `canUseAll` to `canUseAny`
   * MUST  - Same as {@link force} parameter
   */
  canUseAll (names, force) {
    if (names && names.length > 0) {
      if (!Array.isArray(names)) throw new Error('Names should be an array')
      if (names.some(name => name.startsWith('OR '))) {
        return this.canUseAny(names.map(name => name.replace('OR ', '')), force)
      } else {
        return names.every(name => this.canUse(name, force))
      }
    } else {
      return true
    }
  }

  /**
   * Returns true if account is allowed to use any of the specified features
   * @param {Array[String]} names Feature names
   * @param {Boolean} force If true, permission must be granted explicitly
   * @returns {Boolean} True if account is allowed to some of the features
   */
  canUseAny (names, force) {
    if (names && names.length > 0) {
      if (!Array.isArray(names)) throw new Error('Names should be an array')
      const allowed = names.filter(name => this.canUse(name, force))
      return allowed.length === 0 ? false : allowed
    } else {
      return true
    }
  }

  /**
   * Checks if account is allowed to use any of the children
   * of the specified feature.
   * @param {String} name Feature name
   * @param {Boolean} force If true, permission must be granted explicitly
   * @returns {Boolean} true if any of the child features is permitted
   */
  canUseChildrenOf (name, force) {
    if (this.canUse(name)) {
      const { feature } = this.getPermission(name)
      const features = (feature.features || []).map(p => p.name)
      return this.canUseAny(features, force)
    }
  }

  /**
   * Returns true if account is not allowed to use the specified feature
   * @param {String} name Feature name
   * @param {Boolean} force If true, permission must be granted explicitly
   * @returns {Boolean} True if account is not allowed to use the feature
   */
  cannotUse (name, force) {
    return !this.canUse(name, force)
  }

  /**
   * Returns true if account is not allowed to use any of the specified features
   * @param {Array[String]} names Feature names
   * @param {Boolean} force If true, permission must be granted explicitly
   * @returns {Boolean} True if account is not allowed to use all the features
   */
  cannotUseAll (names, force) {
    return !this.canUseAll(names, force)
  }

  /**
   * Returns true if account is not allowed to use some of the specified features
   * @param {Array[String]} names Feature names
   * @param {Boolean} force If true, permission must be granted explicitly
   * @returns {Boolean} True if account is not allowed to use any of the features
   */
  cannotUseAny (names, force) {
    return !this.canUseAny(names, force)
  }

  /**
   * Returns true if account is required to use the specified 'requirement-like' feature.
   * @param {String} name Requirement feature name
   * @returns {Boolean}
   */
  mustUse (name) {
    if (name) {
      const permission = this.permissionDictionary[name]
      if (permission && permission.feature.isRequirement && !permission.implicit && permission.canUse) {
        return true
      }
    }
    return false
  }
}
